import * as stylex from "@stylexjs/stylex";
import type { CSSProperties, FunctionComponent } from "preact";

const colors = {
    primary: "#6499FF",
    success: "#85C525",
    danger: "#FD4E4D",
    gray: "#EEE",
};

const styles = stylex.create({
    miniBase: {
        padding: "3px 5px",
        borderRadius: "3px",
        borderWidth: "1px",
        borderStyle: "solid",
        transition: ".1s ease-in-out background-color",
        fontFamily: "'Inter Variable', sans-serif",
        cursor: "pointer",
    },
    miniPrimary: {
        backgroundColor: {
            default: colors.success,
            ":hover": `color-mix(in prophoto-rgb, ${colors.success} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.success} 95%, #000 5%)`,
        },
        borderColor: `color-mix(in prophoto-rgb, ${colors.success} 50%, #000 50%)`,
        color: "#FFF",
    },
    miniDefault: {
        backgroundColor: {
            default: colors.gray,
            ":hover": `color-mix(in prophoto-rgb, ${colors.gray} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.gray} 95%, #000 5%)`,
        },
        borderColor: `color-mix(in prophoto-rgb, ${colors.gray} 50%, #000 50%)`,
    },
    base: {
        padding: "7px 18px",
        appearance: "none",
        borderWidth: "0",
        borderRadius: "4px",
        borderBottomWidth: "2px",
        transition: ".05s ease-in-out background-color",
        fontFamily: "'Inter Variable', sans-serif",
        cursor: "pointer",
    },
    disabled: {
        cursor: "no-drop",
        filter: "opacity(.5)",
    },
    default: {
        backgroundColor: {
            default: colors.gray,
            ":hover": `color-mix(in prophoto-rgb, ${colors.gray} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.gray} 95%, #000 5%)`,
        },
        color: `color-mix(in prophoto-rgb, ${colors.gray} 35%, #000 65%)`,
        boxShadow: `0 0 2px 0 color-mix(in prophoto-rgb, #000 40%, transparent)`,
        borderColor: {
            default: `color-mix(in prophoto-rgb, ${colors.gray} 90%, #000)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.gray} 50%, #000)`,
        },
    },
    success: {
        backgroundColor: {
            default: colors.success,
            ":hover": `color-mix(in prophoto-rgb, ${colors.success} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.success} 95%, #000 5%)`,
        },
        color: "#FFF",
        borderColor: {
            default: `color-mix(in prophoto-rgb, ${colors.success} 90%, #000)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.success} 50%, #000)`,
        },
    },
    danger: {
        backgroundColor: {
            default: colors.danger,
            ":hover": `color-mix(in prophoto-rgb, ${colors.danger} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.danger} 95%, #000 5%)`,
        },
        color: "#FFF",
        borderColor: {
            default: `color-mix(in prophoto-rgb, ${colors.danger} 90%, #000)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.danger} 50%, #000)`,
        },
    },
    primary: {
        backgroundColor: {
            default: colors.primary,
            ":hover": `color-mix(in prophoto-rgb, ${colors.primary} 95%, #FFF 5%)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.primary} 95%, #000 5%)`,
        },
        color: "#FFF",
        borderColor: {
            default: `color-mix(in prophoto-rgb, ${colors.primary} 90%, #000)`,
            ":active": `color-mix(in prophoto-rgb, ${colors.primary} 50%, #000)`,
        },
    },
});

export interface ButtonProps {
    label: string;
    disabled?: boolean;
    kind?: Exclude<keyof typeof styles, "miniBase" | "base" | "disabled">;
    onClick?: () => void;
    style?: CSSProperties;
}

const Button: FunctionComponent<ButtonProps> = ({
    label,
    disabled = false,
    kind = "default",
    onClick = () => {},
    style = {},
}) => {
    const styleProps = [kind.startsWith("mini") ? styles.miniBase : styles.base, styles[kind]];

    return (
        <button
            style={style}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            {...stylex.props(...styleProps, disabled && styles.disabled)}
        >
            {label}
        </button>
    );
};

export default Button;
